import InboxIcon from '@material-ui/icons/Inbox';
import RestoreIcon from '@material-ui/icons/Restore';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import SendIcon from '@material-ui/icons/Send';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { TicketType } from 'store/models/TicketTypeHelper';

export interface RelatedObjI {
  [status: string]: TicketType[];
}

export declare type RelatedArrayI = {
  status: StatusI;
  ticketType: TicketType;
}[];
export const statuses = [
  'SI_RECEIVED',
  'PREPROCESSING',
  'SI_READY',
  'INDEXING',
  'SENDING',
  'FAILED',
  'SUCCESS',
] as StatusI[];

export declare type StatusI =
  | 'SI_RECEIVED'
  | 'PREPROCESSING'
  | 'SI_READY'
  | 'INDEXING'
  | 'SENDING'
  | 'FAILED'
  | 'SUCCESS';

const hiddenStatuses = ['SI_RECEIVED', 'PREPROCESSING'] as StatusI[];
const noNonStatus = [
  'INDEXING',
  'SENDING',
  'FAILED',
  'SUCCESS',
] as StatusI[];

const statusToWSRequestStatuses: { [status: string]: StatusI[] } = {
  SI_RECEIVED: ['SI_RECEIVED'],
  PREPROCESSING: ['PREPROCESSING'],
  SI_READY: ['SI_READY', 'INDEXING'],
  INDEXING: ['SI_READY', 'INDEXING', 'SENDING'],
  SENDING: ['SENDING', 'FAILED', 'SUCCESS'],
  FAILED: ['FAILED', 'SENDING', 'SUCCESS'],
  SUCCESS: ['SUCCESS', 'SENDING', 'FAILED'],
};
const statusToTicketTypes: { [status: string]: TicketType[] } = {
  SI_RECEIVED: ['SI', 'NON_SI'],
  PREPROCESSING: ['SI', 'NON_SI'],
  SI_READY: ['SI', 'NON_SI'],
  INDEXING: ['SI'],
  SENDING: ['SI'],
  FAILED: ['SI'],
  SUCCESS: ['SI'],
};

export class Statuses {
  static statuses = statuses;
  static statusesDefaultDate = statuses.reduce(
    (acc, status) => ({ ...acc, [status]: null }),
    {},
  );

  static getStatusesMapToDate = (date: string | null) => {
    return statuses.reduce(
      (acc, status) => ({ ...acc, [status]: date }),
      {},
    );
  };
  static hiddenStatuses = hiddenStatuses;
  static mainStatuses = statuses.filter(
    (s) => !hiddenStatuses.includes(s),
  );
  static noNonStatus = noNonStatus;
  static statusToIconMap = {
    SI_RECEIVED: InboxIcon,
    PREPROCESSING: RestoreIcon,
    SI_READY: CloudUploadIcon,
    INDEXING: LocalOfferIcon,
    SENDING: SendIcon,
    FAILED: CancelIcon,
    SUCCESS: CheckCircleIcon,
  };
  static statusToLabel = {
    SI_RECEIVED: 'SI Received',
    PREPROCESSING: 'Preprocessing',
    SI_READY: 'SI Ready',
    INDEXING: 'Indexing',
    SENDING: 'Sending',
    FAILED: 'Failed',
    SUCCESS: 'Success',
  };
  static statusToColor = {
    SI_RECEIVED: '#77ABD1',
    PREPROCESSING: '#F4C967',
    SI_READY: '#ABDAE0',
    INDEXING: '#F2A685',
    SENDING: '#A498C8',
    FAILED: '#B75B5B',
    SUCCESS: '#8FC4A1',
  };
  static getLabelOfStatus(status: StatusI, ticketType: TicketType) {
    if (!(status in this.statusToLabel))
      throw Error(`${status} is not a valid status`);
    return ticketType === 'NON_SI'
      ? this.statusToLabel[status].replace('SI', '')
      : this.statusToLabel[status];
  }
  static getColorOfStatus(status: StatusI) {
    if (!(status in this.statusToColor))
      throw Error(`${status} is not a valid status`);
    return this.statusToColor[status];
  }
  static getIconOfStatus(status: StatusI) {
    if (!(status in this.statusToIconMap))
      throw Error(`${status} is not a valid status`);
    return this.statusToIconMap[status];
  }
  static defaultTicketType = 'SI'; // this will change based on the selected status
  static get defaultStatus() {
    return 'SI_READY';
  }
  static ticketTypes = ['SI', 'NON_SI'];
  static ticketTypeLabelMap = {
    SI: 'SI',
    NON_SI: 'Non',
  };

  static isValidStatus(sStatus: StatusI) {
    return this.statuses.includes(sStatus);
  }
  static isValidTicketType(sType: TicketType) {
    return this.ticketTypes.includes(sType);
  }
  static get ticketTypesForNav() {
    return this.ticketTypes.map((s) => ({
      value: s as TicketType,
      label: this.ticketTypeLabelMap[s],
    }));
  }
  static mainStatusesForStatusNav(ticketType: TicketType) {
    return this._mapMainOrHidden(this.mainStatuses, ticketType);
  }

  static _mapMainOrHidden(
    statuses: StatusI[],
    ticketType: TicketType,
  ) {
    return statuses.map((s) => ({
      value: s,
      label:
        ticketType === 'NON_SI'
          ? this.statusToLabel[s].replace('SI', '')
          : this.statusToLabel[s],
      IconClass: this.statusToIconMap[s],
    }));
  }
  static hiddenStatusesForStatusNav(ticketType: TicketType) {
    return this._mapMainOrHidden(this.hiddenStatuses, ticketType);
  }

  static autoSelectedWhenNon = 'SI_READY' as StatusI;

  static isReadyToViewStatus(status: StatusI) {
    if (!status) return false;
    return !['SI_RECEIVED', 'PREPROCESSING'].includes(status);
  }

  static isInNonStatuses(status: StatusI) {
    return Statuses.noNonStatus.includes(status);
  }

  static canMarkSiNonSi(status: StatusI) {
    return !Statuses.isInNonStatuses(status);
  }

  static canMarkSi(status: StatusI, si: boolean) {
    return Statuses.canMarkSiNonSi(status) && si !== true;
  }

  static canMarkNonSi(status: StatusI, si: boolean) {
    return Statuses.canMarkSiNonSi(status) && si !== false;
  }

  static canAssignUnassign(status: StatusI) {
    return (
      Statuses.isReadyToViewStatus(status) &&
      !['SUCCESS', 'FAILED', 'SENDING'].includes(status)
    );
  }
  static canAssign(status: StatusI, exceptIndexing = false) {
    return (
      Statuses.canAssignUnassign(status) &&
      (!exceptIndexing || status !== 'INDEXING')
    );
  }

  static canUnassign(status: StatusI) {
    return (
      Statuses.canAssignUnassign(status) && status !== 'SI_READY'
    );
  }

  static canSelect(status: StatusI) {
    return (
      Statuses.canMarkSiNonSi(status) ||
      Statuses.canAssignUnassign(status)
    );
  }

  static isInHiddenStatuses(status: StatusI) {
    return Statuses.hiddenStatuses.includes(status);
  }

  static getNewSelectedStatusWhenClickNon(
    currentSelectedStatus: StatusI,
  ): StatusI {
    return !Statuses.noNonStatus.includes(currentSelectedStatus)
      ? currentSelectedStatus
      : Statuses.autoSelectedWhenNon;
  }

  static getWSRequestStatuses(status: StatusI): StatusI[] {
    if (!status || !statuses.includes(status))
      throw Error(`invalid status ${status}`);
    return statusToWSRequestStatuses[status];
  }

  static getWSRequestStatusTicketTypes(
    status: StatusI,
  ): { status: StatusI; ticketType: TicketType }[] {
    const objectVer = this.getWSRequestStatusTicketTypesObj(status);
    return Object.entries(objectVer).reduce(
      (result, [status, ticketTypes]) => {
        result = result.concat(
          ticketTypes.map((ticketType) => ({
            status: status as StatusI,
            ticketType,
          })),
        );
        return result;
      },
      [] as { status: StatusI; ticketType: TicketType }[],
    );
  }

  static getWSRequestStatusTicketTypesObj(
    status: StatusI,
  ): RelatedObjI {
    const statuses = this.getWSRequestStatuses(status);
    return statuses.reduce(
      (result, ss) => ({
        ...result,
        [ss]: statusToTicketTypes[ss],
      }),
      {} as RelatedObjI,
    );
  }
}
